import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { PageEvent } from '@angular/material/paginator';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import {
  ColumnDefinition,
  MasterTableComponent,
  TableAction,
  TableFilterDefinition,
} from '@shared/components/master-table/master-table.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { EmployeeRecord } from '../../organization/employees/employee.interface';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { AttendanceEventRecord } from './attendance-event.interface';

interface EmployeesResponse {
  data?: EmployeeRecord[];
  items?: EmployeeRecord[];
  results?: EmployeeRecord[];
  employees?: EmployeeRecord[];
}

interface CompaniesResponse { items?: CompaniesRecord[]; data?: CompaniesRecord[]; results?: CompaniesRecord[]; companiesRecords?: CompaniesRecord[]; }

interface AttendanceEventsResponse {
  data?: AttendanceEventRecord[];
  items?: AttendanceEventRecord[];
  results?: AttendanceEventRecord[];
  events?: AttendanceEventRecord[];
  pagination?: {
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

@Component({
  selector: 'app-attendance-event',
  standalone: true,
  imports: [BreadcrumbComponent, MasterTableComponent, TranslateModule],
  templateUrl: './attendance-event.component.html',
  styleUrl: './attendance-event.component.scss',
})
export class AttendanceEventComponent implements OnInit {
  dataSource = new MatTableDataSource<AttendanceEventRecord>([]);
  employees: EmployeeRecord[] = [];
  companies: CompaniesRecord[] = [];
  allEvents: AttendanceEventRecord[] = [];
  isLoading = true;
  actions: TableAction<AttendanceEventRecord>[] = [];
  columnDefinitions: ColumnDefinition[] = [];
  tableFilters: TableFilterDefinition<AttendanceEventRecord>[] = [];
  searchValue = '';
  activeFilters: Record<string, string | number | boolean | null | Array<string | number | boolean>> = { source: [] };
  currentPage = 1;
  pageSize = 15;
  totalItems = 0;
  private loadRequestSequence = 0;

  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private searchDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  private employeeSearchDebounceHandle: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.configureTranslations();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.configureTranslations();
      this.tableFilters = this.buildTableFilters();
      this.allEvents = this.allEvents.map((event) => this.normalizeEvent(event));
      if (this.shouldUseClientPagination()) {
        this.applyLocalFilters();
        return;
      }
      this.dataSource.data = this.dataSource.data.map((event) => this.normalizeEvent(event));
    });
    void this.initializeData();
  }

  async initializeData(): Promise<void> {
    this.isLoading = true;
    try {
      await this.loadCompanies();
      await this.loadEmployees();
      this.tableFilters = this.buildTableFilters();
      await this.loadData();
    } finally {
      this.isLoading = false;
    }
  }

  async loadData(): Promise<void> {
    const requestId = ++this.loadRequestSequence;
    this.isLoading = true;
    try {
      if (this.shouldUseClientPagination()) {
        const events = (await this.fetchAllEventPages()).map((event) => this.normalizeEvent(event));
        if (requestId !== this.loadRequestSequence) return;
        this.allEvents = events;
        this.applyLocalFilters();
      } else {
        const response = await this.fetchEventsPage(this.currentPage, this.pageSize);
        if (requestId !== this.loadRequestSequence) return;
        this.allEvents = this.extractEvents(response).map((event) => this.normalizeEvent(event));
        this.totalItems = Array.isArray(response) ? this.allEvents.length : response?.pagination?.total || this.allEvents.length;
        this.dataSource.data = this.allEvents;
      }
      this.tableFilters = this.buildTableFilters();
    } catch (error) {
      if (requestId !== this.loadRequestSequence) return;
      console.error('Error loading attendance events', error);
      this.allEvents = [];
      this.dataSource.data = [];
      this.totalItems = 0;
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('attendanceEventsCatalog.notifications.loadError')),
        'bottom',
        'center'
      );
    } finally {
      if (requestId === this.loadRequestSequence) this.isLoading = false;
    }
  }

  private async fetchEventsPage(page: number, perPage: number): Promise<AttendanceEventRecord[] | AttendanceEventsResponse> {
    return firstValueFrom(
      this.apiService.get<AttendanceEventRecord[] | AttendanceEventsResponse>('attendance/events', {
        params: this.buildRequestParams(page, perPage),
      })
    );
  }

  private async fetchAllEventPages(): Promise<AttendanceEventRecord[]> {
    const perPage = 100;
    const records: AttendanceEventRecord[] = [];
    let page = 1;
    let totalPages = 1;

    do {
      const response = await this.fetchEventsPage(page, perPage);
      records.push(...this.extractEvents(response));
      totalPages = Array.isArray(response) ? 1 : response?.pagination?.totalPages || page;
      page += 1;
    } while (page <= totalPages);

    return records;
  }

  private buildRequestParams(page: number, perPage: number): Record<string, string | number> {
    const activeDay = this.getActiveDay();
    return {
      page,
      perPage,
      ...(this.getActiveCompanyId() ? { companiesRecordId: this.getActiveCompanyId() } : {}),
      ...(this.getActiveEmployeeId() ? { employeeId: this.getActiveEmployeeId() } : {}),
      ...(this.getSingleActiveSource() ? { source: this.getSingleActiveSource() } : {}),
      ...(this.getActiveStatus() ? { status: this.getActiveStatus() } : {}),
      ...(this.getActiveAdministrativeStatus() ? { isActive: this.getActiveAdministrativeStatus() } : {}),
      ...(activeDay ? { dateFrom: this.toDayStartIso(activeDay), dateTo: this.toDayEndIso(activeDay) } : {}),
    };
  }

  private extractEvents(response: AttendanceEventRecord[] | AttendanceEventsResponse | null | undefined): AttendanceEventRecord[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (!response || typeof response !== 'object') {
      return [];
    }

    if (Array.isArray(response.data)) {
      return response.data;
    }

    if (Array.isArray(response.items)) {
      return response.items;
    }

    if (Array.isArray(response.results)) {
      return response.results;
    }

    if (Array.isArray(response.events)) {
      return response.events;
    }

    return [];
  }

  handleAdd(): void {
    if (!this.hasAccess('create')) {
      return;
    }
    void this.router.navigate(['/attendance/attendance-events/new']);
  }

  async handleView(row: AttendanceEventRecord): Promise<void> {
    if (!this.hasAccess('read')) {
      return;
    }
    await this.router.navigate(['/attendance/attendance-events', row.attendanceEventId]);
  }

  handleDelete(row: AttendanceEventRecord): void {
    if (!this.hasAccess('delete')) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('attendanceEventsCatalog.dialog.deleteTitle'),
        message: this.t('attendanceEventsCatalog.dialog.deleteMessage', { event: row.employeeNumber }),
        confirmLabel: this.t('attendanceEventsCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await firstValueFrom(this.apiService.delete(`attendance/events/${row.attendanceEventId}`));
        await this.loadData();
        this.showNotification('snackbar-success', this.t('attendanceEventsCatalog.notifications.deleted'), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting attendance event', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('attendanceEventsCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  handleBulkDelete(rows: AttendanceEventRecord[]): void {
    if (!this.hasAccess('delete') || !rows.length) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('common.bulkDeleteTitle'),
        message: this.t('common.bulkDeleteMessage', { count: rows.length }),
        confirmLabel: this.t('common.delete'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await Promise.all(rows.map((row) => firstValueFrom(this.apiService.delete(`attendance/events/${row.attendanceEventId}`))));
        await this.loadData();
        this.showNotification('snackbar-success', this.t('common.bulkDeleteSuccess', { count: rows.length }), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting attendance events', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('attendanceEventsCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  onSearchChange(search: string): void {
    this.searchValue = search.trim().toLowerCase();
    this.currentPage = 1;
    if (this.searchDebounceHandle) {
      clearTimeout(this.searchDebounceHandle);
    }
    this.searchDebounceHandle = setTimeout(() => {
      void this.loadData();
    }, 250);
  }

  onFiltersChange(filters: Record<string, string | number | boolean | null | Array<string | number | boolean>>): void {
    const previousCompanyId = this.getActiveCompanyId();
    this.activeFilters = filters;
    this.currentPage = 1;
    if (previousCompanyId !== this.getActiveCompanyId()) {
      this.activeFilters['employee'] = null;
      void this.loadEmployees().then(() => this.loadData());
      return;
    }
    void this.loadData();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    void this.loadData();
  }

  onFilterSearchChange(event: { filterId: string; search: string }): void {
    if (event.filterId !== 'employee') {
      return;
    }

    if (this.employeeSearchDebounceHandle) {
      clearTimeout(this.employeeSearchDebounceHandle);
    }

    this.employeeSearchDebounceHandle = setTimeout(() => {
      void this.loadEmployees(event.search);
    }, 250);
  }

  private async loadEmployees(search = ''): Promise<void> {
    try {
      const selectedEmployeeId = this.getActiveEmployeeId();
      const selectedEmployee = this.employees.find((employee) => employee.employeeId === selectedEmployeeId);
      const response = await firstValueFrom(
        this.apiService.get<EmployeeRecord[] | EmployeesResponse>('employees', {
          params: { page: 1, perPage: 20, isActive: 'true', ...(this.getActiveCompanyId() ? { companiesRecordId: this.getActiveCompanyId() } : {}), ...(search ? { search } : {}) },
        })
      );
      const nextEmployees = this.extractEmployees(response)
        .filter((employee) => employee.isActive !== false)
        .sort((left, right) => (left.fullName || '').localeCompare(right.fullName || ''));

      this.employees = selectedEmployee && !nextEmployees.some((employee) => employee.employeeId === selectedEmployee.employeeId)
        ? [selectedEmployee, ...nextEmployees]
        : nextEmployees;

      this.tableFilters = this.buildTableFilters();
    } catch (error) {
      console.error('Error loading employees for attendance events', error);
      this.employees = [];
    }
  }

  private extractEmployees(response: EmployeeRecord[] | EmployeesResponse): EmployeeRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.employees || [];
  }

  private async loadCompanies(): Promise<void> {
    const response = await firstValueFrom(this.apiService.get<CompaniesRecord[] | CompaniesResponse>('companies-records', { params: { isActive: 'true' } }));
    this.companies = (Array.isArray(response) ? response : response.items || response.data || response.results || response.companiesRecords || [])
      .filter((company) => company.isActive !== false)
      .sort((left, right) => left.name.localeCompare(right.name));
  }

  private applyLocalFilters(): void {
    const search = this.searchValue;
    const activeSources = this.getActiveSources();
    const activeDay = this.getActiveDay();
    const activeCompanyId = this.getActiveCompanyId();
    const filteredEvents = this.allEvents.filter((event) => {
      if (activeCompanyId && event.companiesRecordId !== activeCompanyId) return false;
      const matchesSource = !activeSources.length || activeSources.includes(event.source);
      if (!matchesSource) {
        return false;
      }

      if (activeDay && this.toDateKey(event.eventAt, event.timezone) !== activeDay) {
        return false;
      }

      if (!search) {
        return true;
      }

      const haystack = [
        event.employeeNumber,
        event.employeeLabel,
        event.sourceLabel,
        event.eventTypeLabel,
        event.normalizedEventTypeLabel,
        event.statusLabel,
        event.deviceId,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(search);
    });
    this.totalItems = filteredEvents.length;
    const maxPage = Math.max(Math.ceil(this.totalItems / this.pageSize), 1);
    if (this.currentPage > maxPage) {
      this.currentPage = 1;
    }
    const start = (this.currentPage - 1) * this.pageSize;
    this.dataSource.data = filteredEvents.slice(start, start + this.pageSize);
  }

  private normalizeEvent(event: Partial<AttendanceEventRecord>): AttendanceEventRecord {
    const source = `${event.source || 'device'}`.toLowerCase();
    const eventType = event.eventType || '';
    const normalizedEventType = event.normalizedEventType || null;
    const status = event.status || '';
    const faceValidationStatus = event.faceValidationStatus || null;
    const isActive = event.isActive !== false;
    const employee = this.employees.find((item) => item.employeeId === event.employeeId);
    const eventAt = event.eventAt || event.eventAtUtc || '';
    return {
      attendanceEventId: event.attendanceEventId || crypto.randomUUID(),
      center: event.center || '',
      employeeId: event.employeeId || '',
      employeeNumber: event.employeeNumber || employee?.employeeNumber || '-',
      employeeName: event.employeeName || employee?.fullName || event.employeeNumber || '-',
      companiesRecordId: event.companiesRecordId || '',
      orgUnitId: event.orgUnitId || null,
      orgPath: event.orgPath || null,
      source,
      eventType,
      normalizedEventType,
      eventAt,
      timezone: event.timezone || 'America/Cancun',
      deviceId: event.deviceId || null,
      deviceVersion: event.deviceVersion || null,
      lat: event.lat ?? null,
      lng: event.lng ?? null,
      accuracy: event.accuracy ?? null,
      faceValidationStatus,
      status,
      rawPayload: event.rawPayload || null,
      isActive,
      employeeLabel: event.employeeName || employee?.fullName || event.employeeNumber || '-',
      sourceLabel: this.t(`attendanceEventsCatalog.sources.${source}`),
      eventTypeLabel: eventType ? this.t(`attendanceEventsCatalog.eventTypes.${eventType}`) : '-',
      normalizedEventTypeLabel: normalizedEventType ? this.t(`attendanceEventsCatalog.eventTypes.${normalizedEventType}`) : '-',
      eventAtLabel: this.formatDateTime(eventAt, event.timezone || 'America/Cancun'),
      statusLabel: status ? this.t(`attendanceEventsCatalog.statuses.${status}`) : '-',
      faceValidationStatusLabel: faceValidationStatus ? this.t(`attendanceEventsCatalog.faceStatuses.${faceValidationStatus}`) : '-',
      activeLabel: isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
    };
  }

  private buildTableFilters(): TableFilterDefinition<AttendanceEventRecord>[] {
    return [
      {
        id: 'company', label: this.t('orgUnitsCatalog.fields.company'), icon: 'business', searchable: true,
        value: this.activeFilters['company'] ?? null,
        options: this.companies.map((company) => ({ value: company.companiesRecordId, label: company.name, searchText: `${company.code || ''} ${company.center || ''}`.trim() })),
      },
      {
        id: 'employee',
        label: this.t('attendanceEventsCatalog.filters.employee'),
        placeholder: this.t('attendanceEventsCatalog.filters.employeePlaceholder'),
        icon: 'badge',
        searchable: true,
        value: this.activeFilters['employee'] ?? null,
        options: this.employees.map((employee) => ({
          value: employee.employeeId,
          label: employee.fullName || employee.employeeNumber,
          searchText: employee.employeeNumber,
        })),
      },
      {
        id: 'day',
        label: this.t('attendanceEventsCatalog.filters.day'),
        placeholder: this.t('attendanceEventsCatalog.filters.dayPlaceholder'),
        icon: 'calendar_today',
        type: 'date',
        value: this.activeFilters['day'] ?? null,
      },
      {
        id: 'source',
        label: this.t('attendanceEventsCatalog.filters.source'),
        placeholder: this.t('attendanceEventsCatalog.filters.sourcePlaceholder'),
        icon: 'memory',
        searchable: false,
        multiple: true,
        value: this.activeFilters['source'] ?? [],
        options: ['web', 'mobile', 'device', 'kiosk'].map((source) => ({
          value: source,
          label: this.t(`attendanceEventsCatalog.sources.${source}`),
        })),
      },
      {
        id: 'status',
        label: this.t('attendanceEventsCatalog.filters.status'),
        placeholder: this.t('attendanceEventsCatalog.filters.statusPlaceholder'),
        icon: 'rule',
        searchable: false,
        value: this.activeFilters['status'] ?? null,
        options: ['pending_analysis', 'analyzed', 'rejected'].map((status) => ({
          value: status,
          label: this.t(`attendanceEventsCatalog.statuses.${status}`),
        })),
      }
    ];
  }

  private configureTranslations(): void {
    this.actions = [
      {
        id: 'add',
        icon: 'add',
        tooltip: this.t('attendanceEventsCatalog.actions.create'),
        colorClass: 'success',
        auth: { resource: 'attendanceEvents', action: 'create' },
        behavior: 'add',
      },
      {
        id: 'details',
        icon: 'visibility',
        tooltip: this.t('attendanceEventsCatalog.actions.view'),
        colorClass: 'info',
        auth: { resource: 'attendanceEvents', action: 'read' },
        selectionMode: 'single',
      },
      {
        id: 'delete',
        icon: 'delete_outline',
        tooltip: this.t('attendanceEventsCatalog.actions.delete'),
        colorClass: 'danger',
        auth: { resource: 'attendanceEvents', action: 'delete' },
        behavior: 'delete',
        selectionMode: 'single',
      },
    ];

    this.columnDefinitions = [
      { def: 'select', label: this.t('common.select'), type: 'check', visible: true },
      { def: 'employeeNumber', label: this.t('attendanceEventsCatalog.fields.employeeNumber'), type: 'text', visible: true },
      { def: 'employeeName', label: this.t('attendanceEventsCatalog.fields.employee'), type: 'text', visible: true },
      { def: 'sourceLabel', label: this.t('attendanceEventsCatalog.fields.source'), type: 'text', visible: true },
      { def: 'eventTypeLabel', label: this.t('attendanceEventsCatalog.fields.eventType'), type: 'text', visible: true },
      { def: 'normalizedEventTypeLabel', label: this.t('attendanceEventsCatalog.fields.normalizedEventType'), type: 'text', visible: true },
      { def: 'eventAtLabel', label: this.t('attendanceEventsCatalog.fields.eventAt'), type: 'text', visible: true },
      { def: 'statusLabel', label: this.t('attendanceEventsCatalog.fields.status'), type: 'status', visible: true },
    ];

    this.tableFilters = this.buildTableFilters();
  }

  private getActiveEmployeeId(): string {
    return (this.activeFilters['employee'] as string) || '';
  }

  private getActiveCompanyId(): string { return (this.activeFilters['company'] as string) || ''; }

  private getActiveSources(): string[] {
    const value = this.activeFilters['source'];
    if (Array.isArray(value)) {
      return value.map((item) => `${item}`);
    }

    return value ? [`${value}`] : [];
  }

  private getSingleActiveSource(): string {
    const sources = this.getActiveSources();
    return sources.length === 1 ? sources[0] : '';
  }

  private getActiveStatus(): string {
    return (this.activeFilters['status'] as string) || '';
  }

  private getActiveAdministrativeStatus(): string {
    return (this.activeFilters['administrativeStatus'] as string) || '';
  }

  private getActiveDay(): string {
    return (this.activeFilters['day'] as string) || '';
  }

  private shouldUseClientPagination(): boolean {
    return Boolean(this.searchValue || this.getActiveDay() || this.getActiveSources().length > 1);
  }

  private toDateKey(value?: string | null, timezone = 'America/Cancun'): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const getPart = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value || '';

    return `${getPart('year')}-${getPart('month')}-${getPart('day')}`;
  }

  private toDayStartIso(day: string): string {
    const [year, month, date] = day.split('-').map(Number);
    return new Date(year, month - 1, date, 0, 0, 0, 0).toISOString();
  }

  private toDayEndIso(day: string): string {
    const [year, month, date] = day.split('-').map(Number);
    return new Date(year, month - 1, date, 23, 59, 59, 999).toISOString();
  }

  private formatDateTime(value?: string | null, timezone?: string): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private hasAccess(action: string): boolean {
    return this.authorizationService.hasAccess({ resource: 'attendanceEvents', action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2200,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
